import { METRICS_COLUMNS } from '../constants';

/**
 * Shared utilities for identifying and working with experiment view column types
 */

export type ColumnType =
  | 'inputs'
  | 'outputs'
  | 'reference_outputs'
  | 'attachments'
  | 'feedback'
  | 'metrics'
  | 'metadata'
  | 'unknown';

/**
 * Checks if a column matches a specific type (exact or prefixed)
 */
export function isColumnType(columnName: string, type: string): boolean {
  return columnName === type || columnName.startsWith(`${type}.`);
}

/**
 * Determines the base type of a column name
 */
export function getColumnType(columnName: string): ColumnType {
  // Special case for attachments (no sub-columns)
  if (columnName === 'attachments') {
    return 'attachments';
  }

  // Check core column types that support sub-columns
  const coreTypes: ColumnType[] = ['inputs', 'outputs', 'reference_outputs'];
  for (const type of coreTypes) {
    if (isColumnType(columnName, type)) {
      return type;
    }
  }

  // Check prefix-only types
  if (columnName.startsWith('feedback.')) {
    return 'feedback';
  }
  if (columnName.startsWith('metrics.')) {
    return 'metrics';
  }
  if (columnName.startsWith('metadata.')) {
    return 'metadata';
  }

  return 'unknown';
}

/**
 * Extracts the key from a dotted column name (e.g., 'feedback.score' -> 'score')
 */
export function extractPrefixedKey(columnName: string): string {
  const parts = columnName.split('.');
  return parts.length > 1 ? parts.slice(1).join('.') : columnName;
}

/**
 * Core columns that cannot be removed, only hidden
 */
export const CORE_COLUMNS = [
  'inputs',
  'reference_outputs',
  'outputs',
  'attachments',
];

/**
 * Valid column prefixes for validation
 */
export const VALID_COLUMN_PREFIXES = [
  ...CORE_COLUMNS,
  'feedback',
  'metrics',
  'metadata',
];

export const DEFAULT_METRIC_NAMES = Object.keys(METRICS_COLUMNS);

export const EXAMPLE_METADATA_PREFIX = 'metadata.ls_example_';

export function getColumnDisplayName(columnName: string): string {
  if (
    getColumnType(columnName) === 'metadata' &&
    columnName.startsWith(EXAMPLE_METADATA_PREFIX)
  ) {
    return `example.metadata.${columnName.slice(
      EXAMPLE_METADATA_PREFIX.length
    )}`;
  } else if (columnName.startsWith('metrics.')) {
    const metricName = columnName.substring('metrics.'.length);
    if (DEFAULT_METRIC_NAMES.includes(metricName)) {
      return metricName;
    }
  }
  return columnName;
}

export function getColumnStorageName(displayName: string): string {
  if (displayName.startsWith('example.metadata.')) {
    return `metadata.ls_example_${displayName.slice(
      'example.metadata.'.length
    )}`;
  } else if (DEFAULT_METRIC_NAMES.includes(displayName)) {
    return `metrics.${displayName}`;
  }
  return displayName;
}

export function getColumnShortName(displayName: string): string {
  let shortName = displayName.split('.').slice(1).join('.') || displayName;
  if (shortName.startsWith('ls_example_')) {
    shortName = shortName.slice('ls_example_'.length);
  }
  return shortName;
}
